import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../shared/utils/request";
import PageLoader from "../shared/pageLoader";
import LoginWarning from "../shared/warningPages";
import AccountSidebar from "../shared/accountSidebar";
import { usePageSeo } from "../../shared/seo";
import { toast } from "react-hot-toast";

const MyAccount = () => {
  usePageSeo("myAccount");
  const isToken = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });

  useEffect(() => {
    if (isToken) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [isToken]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await api.get("customer/profile");
      if (data?.status && data?.data) {
        setProfile(data.data);
        setForm({ name: data.data.name || "", email: data.data.email || "" });
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSaving(true);
    try {
      const data = await api.post("customer/updateProfile", {
        name: form.name.trim(),
        email: form.email.trim() || null,
      });
      if (data?.status) {
        setProfile((prev) => ({ ...prev, ...data.data }));
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({ ...stored, ...data.data }));
        toast.success(data?.message || "Profile updated successfully");
      } else {
        const firstError = data?.errors
          ? Object.values(data.errors)[0]?.[0]
          : null;
        toast.error(firstError || data?.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const initials = (profile?.name || "U")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const addresses = profile?.addresses || [];

  if (!isToken && !loading) return <LoginWarning />;

  return (
    <>
      {loading && <PageLoader />}

      <section
        className="py-40"
        style={{ background: "linear-gradient(135deg, #f6faf7 0%, #edf5f0 100%)" }}
      >
        <div className="container container-lg">
          <div className="row g-3">
            <div className="col-lg-3 mb-16 mb-lg-0">
              <AccountSidebar />
            </div>

            <div className="col-lg-9">
              {profile && (
                <>
                  <div className="mb-16">
                    <h3 className="mb-2 fw-bold text-main-700">My Account</h3>
                    <p className="text-neutral-600 text-sm mb-0 d-none d-sm-block">
                      Manage your personal information
                    </p>
                  </div>

                  {/* Profile Card */}
                  <div
                    className="rounded-16 overflow-hidden bg-white border border-main-100 mb-16"
                    style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.06)" }}
                  >
                    <div
                      className="px-16 px-sm-24 py-16"
                      style={{
                        background: "linear-gradient(135deg, #1a6b30 0%, #228b34 50%, #2ea44f 100%)",
                      }}
                    >
                      <div className="d-flex align-items-center gap-12 flex-wrap">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold text-white"
                          style={{
                            width: "64px",
                            height: "64px",
                            background: "rgba(255,255,255,0.2)",
                            border: "2px solid rgba(255,255,255,0.3)",
                            fontSize: "22px",
                          }}
                        >
                          {initials}
                        </div>
                        <div>
                          <h5 className="mb-1 fw-bold text-white" style={{ fontSize: "1.05rem" }}>
                            {profile?.name || "Customer"}
                          </h5>
                          <p className="mb-0 text-white d-flex align-items-center gap-6 flex-wrap" style={{ fontSize: "12px", opacity: 0.9 }}>
                            <span className="d-inline-flex align-items-center gap-4">
                              <i className="ph ph-envelope-simple" />
                              {profile?.email || "No email added"}
                            </span>
                            <span className="opacity-50">•</span>
                            <span className="d-inline-flex align-items-center gap-4">
                              <i className="ph ph-phone" />
                              {profile?.phone || "—"}
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-white text-main-700 px-10 py-6 rounded-pill ms-auto" style={{ fontSize: "11px" }}>
                          <i className="ph ph-check-circle me-4" />
                          Active
                        </span>
                      </div>
                    </div>

                    <div className="row g-0 text-center">
                      <div className="col-4 py-12" style={{ borderRight: "1px solid #eef5f0" }}>
                        <h6 className="mb-1 fw-bold text-main-700">{addresses.length}</h6>
                        <p className="mb-0 text-neutral-500 text-xs">Addresses</p>
                      </div>
                      <div className="col-4 py-12" style={{ borderRight: "1px solid #eef5f0" }}>
                        <h6 className="mb-1 fw-bold text-main-700">{profile?.orders_count ?? "—"}</h6>
                        <p className="mb-0 text-neutral-500 text-xs">Orders</p>
                      </div>
                      <div className="col-4 py-12">
                        <h6 className="mb-1 fw-bold text-main-700">
                          {profile?.created_at
                            ? new Date(profile.created_at).getFullYear()
                            : "—"}
                        </h6>
                        <p className="mb-0 text-neutral-500 text-xs">Member Since</p>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    {/* Personal Information */}
                    <div className="col-md-7">
                      <div
                        className="rounded-12 bg-white border border-main-100 h-100"
                        style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}
                      >
                        <div className="px-12 px-sm-16 py-10" style={{ borderBottom: "1px solid #eef5f0" }}>
                          <h6 className="mb-0 fw-bold text-main-700" style={{ fontSize: "0.95rem" }}>
                            <i className="ph ph-user-circle me-8 text-main-600" />
                            Personal Information
                          </h6>
                        </div>
                        <form onSubmit={handleSubmit}>
                          <div className="px-12 px-sm-16 py-12">
                            <div className="mb-12">
                              <label className="form-label fw-semibold text-neutral-700" style={{ fontSize: "13px" }}>
                                Full Name <span className="text-danger">*</span>
                              </label>
                              <div className="position-relative">
                                <i className="ph ph-user position-absolute text-neutral-400" style={{ left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "16px" }} />
                                <input
                                  type="text"
                                  name="name"
                                  className="form-control rounded-10"
                                  style={{ paddingLeft: "38px", fontSize: "14px" }}
                                  value={form.name}
                                  onChange={handleChange}
                                  placeholder="Enter your name"
                                />
                              </div>
                            </div>

                            <div className="mb-12">
                              <label className="form-label fw-semibold text-neutral-700" style={{ fontSize: "13px" }}>
                                Email Address
                              </label>
                              <div className="position-relative">
                                <i className="ph ph-envelope-simple position-absolute text-neutral-400" style={{ left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "16px" }} />
                                <input
                                  type="email"
                                  name="email"
                                  className="form-control rounded-10"
                                  style={{ paddingLeft: "38px", fontSize: "14px" }}
                                  value={form.email}
                                  onChange={handleChange}
                                  placeholder="Enter your email"
                                />
                              </div>
                            </div>

                            <div className="mb-12">
                              <label className="form-label fw-semibold text-neutral-700" style={{ fontSize: "13px" }}>
                                Phone Number
                              </label>
                              <div className="position-relative">
                                <i className="ph ph-phone position-absolute text-neutral-400" style={{ left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "16px" }} />
                                <input
                                  type="text"
                                  className="form-control rounded-10"
                                  style={{ paddingLeft: "38px", fontSize: "14px", background: "#f6faf7", color: "#98a2b3" }}
                                  value={profile?.phone || "—"}
                                  readOnly
                                  disabled
                                />
                              </div>
                            </div>

                            <button
                              type="submit"
                              className="btn btn-main w-100 d-inline-flex align-items-center justify-content-center gap-6 rounded-10 fw-semibold mt-8"
                              style={{ padding: "12px", transform: "none" }}
                              disabled={saving}
                            >
                              {saving ? (
                                <>
                                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <i className="ph ph-check-circle" />
                                  Save Changes
                                </>
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>

                    {/* Address Summary */}
                    <div className="col-md-5">
                      <div
                        className="rounded-12 bg-white border border-main-100 h-100"
                        style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}
                      >
                        <div className="d-flex align-items-center justify-content-between px-12 px-sm-16 py-10" style={{ borderBottom: "1px solid #eef5f0" }}>
                          <h6 className="mb-0 fw-bold text-main-700" style={{ fontSize: "0.95rem" }}>
                            <i className="ph ph-map-pin me-8 text-danger" />
                            Saved Addresses
                          </h6>
                          <span className="badge bg-main-50 text-main-700 px-8 py-4 rounded-pill" style={{ fontSize: "11px" }}>
                            {addresses.length}
                          </span>
                        </div>
                        <div className="px-12 px-sm-16 py-12">
                          {addresses.length ? (
                            <div className="d-flex flex-column gap-8">
                              {addresses.slice(0, 2).map((addr) => (
                                <div key={addr.id} className="rounded-10 p-10" style={{ background: "#f6faf7", border: "1px solid #eef5f0" }}>
                                  <p className="mb-1 fw-semibold text-neutral-800 text-truncate" style={{ fontSize: "13px" }}>
                                    <i className="ph ph-house me-6 text-main-600" />
                                    {addr.name}
                                  </p>
                                  <p className="mb-0 text-neutral-500" style={{ fontSize: "12px" }}>
                                    {[addr.address_line_1, addr.address_line_2].filter(Boolean).join(", ")}
                                  </p>
                                  <p className="mb-0 text-neutral-500" style={{ fontSize: "12px" }}>
                                    {[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-16">
                              <i className="ph ph-map-pin text-neutral-300" style={{ fontSize: "2rem" }} />
                              <p className="text-neutral-500 mb-8 mt-8" style={{ fontSize: "0.8rem" }}>
                                No saved addresses yet
                              </p>
                            </div>
                          )}
                          <Link
                            to="/address-book"
                            className="d-inline-flex align-items-center gap-6 text-main-600 text-decoration-none fw-semibold mt-12"
                            style={{ fontSize: "13px" }}
                          >
                            <i className="ph ph-plus" />
                            Manage Addresses
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MyAccount;
